import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { OpenService } from '@patsnap/tahiti-core/services/open-service';
import { isUserLogin } from '@eureka/utils/account';
import type { AuthService } from '../auth-service';
import { ShareAccessCheckRepository, ShareOwnerInfoRepository } from '../share-tools-repository';
import type { AuthModuleData, AuthPageParams, ShareAuthCheckData } from '../../types';
import { AuthModel } from '../../models/auth.model';
import {
  ejectAirShareInterceptor,
  injectAirShareInterceptor,
} from '@eureka/services/src/apis/interceptors/share.interceptor';
import { Site, SLD } from '@patsnap-utils/biz';

@injectable()
export class AuthServiceImpl implements AuthService {
  private _pageParams: AuthPageParams = {
    currentPageType: '',
    currentDetailId: '',
  };

  // eslint-disable-next-line no-useless-constructor
  constructor(
    @inject(ShareAccessCheckRepository)
    private _shareAccessCheckRepository: ShareAccessCheckRepository,
    @inject(ShareOwnerInfoRepository) private _shareOwnerInfoRepository: ShareOwnerInfoRepository,
    @inject(AuthModel) private _authModel: AuthModel,
    @inject(OpenService) private _openService: OpenService,
  ) {}

  private async _getOwnerInfo(data: ShareAuthCheckData) {
    try {
      const response = await this._shareOwnerInfoRepository.getShareOwnerInfo(data);
      this._updateAuthState({
        ownerName: response.ownerName,
        ownerId: response.ownerId,
        canCopy: response.canCopy,
        canCreateShare: response.canCreateShare,
      });
      return response;
    } catch (err) {
      this._handleError('Failed to get owner info', err);
      return { ownerName: '', ownerId: '', canCopy: false, canCreateShare: false };
    }
  }

  private _updateAuthState(state: Partial<AuthModuleData>) {
    this._authModel.updateModuleData((v) => ({
      ...v,
      ...state,
    }));
  }

  private _backToHome() {
    const url = new Site({ sld: SLD.EUREKA }).href;
    this._openService.open(url, {
      target: '_self',
    });
  }

  private _handleError(message: string, error: any) {
    console.error(message, error);
    // Additional logging or error handling can be added here
  }

  async shareAccessCheck(data?: ShareAuthCheckData) {
    try {
      const isLogin = await isUserLogin();
      this._updateAuthState({ isLogin });

      const isShare = await this._determineShareAccess(isLogin, data);

      if (isShare || !isLogin) {
        injectAirShareInterceptor();
        this._updateAuthState({ isShare });
        if (data) {
          this._getOwnerInfo(data);
        }
      } else {
        this._denyAccess();
      }

      return isShare;
    } catch (err) {
      this._handleError('Share access check failed', err);
      this._denyAccess();
      this._backToHome();
      throw err;
    }
  }

  private async _determineShareAccess(
    isLogin: boolean,
    data?: ShareAuthCheckData,
  ): Promise<boolean> {
    if (data?.data_id && data?.data_type) {
      return isLogin
        ? await this._shareAccessCheckRepository.shareAccessCheck(data)
        : await this._shareAccessCheckRepository.shareAccessCheckForGuest(data);
    }
    return false;
  }

  private _denyAccess() {
    ejectAirShareInterceptor();
    this._updateAuthState({
      isShare: false,
      ownerName: '',
      canCopy: false,
      canCreateShare: false,
    });
  }

  get pageParams() {
    return this._pageParams;
  }

  setPageParams(params: Partial<typeof this._pageParams>) {
    this._pageParams = {
      ...this._pageParams,
      ...params,
    };
  }
}
